import * as SliderPrimitive from '@radix-ui/react-slider'
import { useState } from 'react'
import z from 'zod'
import * as styles from './slider.css'

export const SliderThumbStyleSchema = z.enum(['circle', 'triangle'])
export type SliderThumbStyle = z.infer<typeof SliderThumbStyleSchema>

type SliderProps = {
  min?: number
  max?: number
  step?: number
  value: [number, number]
  onValueChange: (value: [number, number]) => unknown
  'aria-label'?: string
  label?: string
  thumbStyle?: SliderThumbStyle
}

const formatNumber = (num: number) => {
  return Number.isInteger(num) ? num.toString() : num.toFixed(1)
}

const EDITING_KEYS = new Set<React.KeyboardEvent['key']>([
  'ArrowLeft',
  'ArrowRight',
  'ArrowUp',
  'ArrowDown',
  'PageUp',
  'PageDown',
  'Home',
  'End',
])

// Radix focuses the thumb on pointer-down, so a finished mouse-drag leaves the
// thumb focused (and :focus-visible) — which would keep the tooltip pinned.
// Track interaction explicitly instead: active during a drag (cleared when the
// pointer capture is released) or during keyboard editing (cleared on blur).
const useThumbActive = () => {
  const [isActive, setIsActive] = useState(false)
  const thumbProps = {
    onPointerDown: () => setIsActive(true),
    onLostPointerCapture: () => setIsActive(false),
    onKeyDown: (event: React.KeyboardEvent) => {
      if (EDITING_KEYS.has(event.key)) {
        setIsActive(true)
      }
    },
    onBlur: () => setIsActive(false),
  }

  return { isActive, thumbProps }
}

const renderStepLabels = (min: number, max: number, labelStep: number, currentValue: [number, number]) => {
  const steps: number[] = []

  for (let i = min; i <= max; i += labelStep) {
    steps.push(i)
  }

  return (
    <div className={styles.stepsContainer}>
      {steps.map((value, index) => (
        <span
          key={value}
          className={`${styles.stepLabel} ${value >= currentValue[0] && value <= currentValue[1] ? styles.stepLabelSelected : styles.stepLabelUnSelected}`}
          style={
            {
              '--left': `${(index * 100) / (steps.length - 1)}%`,
            } as React.CSSProperties
          }
        >
          {formatNumber(value)}
        </span>
      ))}
    </div>
  )
}

const CircleThumb: React.FC<{ value: number }> = ({ value }) => {
  const { isActive, thumbProps } = useThumbActive()

  return (
    <SliderPrimitive.Thumb className={styles.sliderThumb} data-active={isActive ? '' : undefined} {...thumbProps}>
      <div className={styles.thumbTooltip}>{formatNumber(value)}</div>
    </SliderPrimitive.Thumb>
  )
}

const TriangleThumb: React.FC<{ value: number; direction: 'left' | 'right' }> = ({ value, direction }) => {
  const { isActive, thumbProps } = useThumbActive()

  return (
    <SliderPrimitive.Thumb
      className={styles.sliderThumbTriangle}
      data-active={isActive ? '' : undefined}
      {...thumbProps}
    >
      <svg
        width="16"
        height="24"
        viewBox="0 0 16 24"
        className={styles.sliderThumbTriangleSvg}
        style={{
          transform: direction === 'right' ? 'scaleX(-1) translateX(-1px)' : undefined,
        }}
      >
        <path d="M2 2L14 12L2 22Z" strokeWidth="2" strokeLinejoin="round" />
      </svg>
      <div className={styles.thumbTooltip}>{formatNumber(value)}</div>
    </SliderPrimitive.Thumb>
  )
}

export const Slider = ({
  min = 0,
  max = 100,
  step,
  value,
  onValueChange,
  'aria-label': ariaLabel,
  label,
  thumbStyle = 'circle',
}: SliderProps) => {
  const labelStep = (max - min) / 10
  const actualStep = step ?? labelStep

  return (
    <div className={styles.container}>
      {label && <label className={styles.label}>{label}</label>}
      <SliderPrimitive.Root
        className={styles.sliderRoot}
        min={min}
        max={max}
        step={actualStep}
        value={value}
        onValueChange={onValueChange}
        aria-label={ariaLabel}
      >
        <SliderPrimitive.Track className={styles.sliderTrack}>
          <SliderPrimitive.Range className={styles.sliderRange} />
        </SliderPrimitive.Track>
        {thumbStyle === 'circle' ? (
          <>
            <CircleThumb value={value[0]} />
            <CircleThumb value={value[1]} />
          </>
        ) : (
          <>
            <TriangleThumb value={value[0]} direction="left" />
            <TriangleThumb value={value[1]} direction="right" />
          </>
        )}
      </SliderPrimitive.Root>
      {renderStepLabels(min, max, labelStep, value)}
    </div>
  )
}
